import { useState } from "react";
import { toast } from "sonner";
import { MapPin, Mail, Phone } from "lucide-react";
import { Reveal, SectionLabel } from "@/components/motion";
import { TextField, TextAreaField } from "@/components/Field";
import { submitMessage } from "@/lib/api";
import { CONTACT } from "@/lib/data";

const EMPTY = { name: "", email: "", subject: "", message: "" };

export default function Contact() {
  const [form, setForm] = useState(EMPTY);
  const [sending, setSending] = useState(false);
  const onChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const onSubmit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await submitMessage(form);
      toast.success("Message sent. We'll get back to you shortly.");
      setForm(EMPTY);
    } catch {
      toast.error("Message failed to send. Please email us directly.");
    } finally {
      setSending(false);
    }
  };

  return (
    <main className="pt-16" data-testid="contact-page">
      <section className="border-b border-line">
        <div className="mx-auto max-w-7xl px-5 py-20 lg:px-8">
          <SectionLabel>// Get In Touch</SectionLabel>
          <h1 className="mt-3 max-w-3xl font-display text-4xl font-black uppercase leading-[1.05] tracking-tight text-bright sm:text-5xl lg:text-6xl">
            Talk to <span className="text-hazard">dispatch.</span>
          </h1>
          <p className="mt-5 max-w-xl text-base leading-relaxed text-[#9CA3AF] sm:text-lg">
            Shippers — send your freight requirement. Carriers — introduce your equipment, operating area and availability.
          </p>
        </div>
      </section>

      <section className="mx-auto grid max-w-7xl gap-12 px-5 py-20 lg:grid-cols-5 lg:px-8">
        <Reveal className="lg:col-span-2">
          <div className="flex flex-col gap-5">
            <a href={`mailto:${CONTACT.dispatch}`} data-testid="contact-email-card" className="group border border-line bg-panel p-7 transition-colors hover:bg-panel-hover">
              <Mail size={22} className="text-hazard" />
              <p className="mt-4 font-mono text-[11px] uppercase tracking-[0.25em] text-brass">Email — Quotes &amp; Dispatch</p>
              <p className="mt-2 break-all font-mono text-sm text-bright group-hover:text-hazard">{CONTACT.dispatch}</p>
            </a>
            <a href={CONTACT.phoneHref} data-testid="contact-phone-card" className="group border border-line bg-panel p-7 transition-colors hover:bg-panel-hover">
              <Phone size={22} className="text-hazard" />
              <p className="mt-4 font-mono text-[11px] uppercase tracking-[0.25em] text-brass">Phone</p>
              <p className="mt-2 font-mono text-sm text-bright group-hover:text-hazard">{CONTACT.phone}</p>
            </a>
            <div className="border border-line bg-panel p-7" data-testid="contact-location-card">
              <MapPin size={22} className="text-hazard" />
              <p className="mt-4 font-mono text-[11px] uppercase tracking-[0.25em] text-brass">Headquarters</p>
              <p className="mt-2 font-mono text-sm text-bright">4430 112 Ave SE</p>
              <p className="mt-0.5 font-mono text-sm text-bright">Calgary, Alberta T2C 2K2, Canada</p>
              <p className="mt-1 font-mono text-xs text-dim">Serving Canada &amp; North American markets</p>
            </div>
          </div>
        </Reveal>

        <Reveal className="lg:col-span-3" delay={0.1}>
          <form onSubmit={onSubmit} className="border border-line bg-panel" data-testid="contact-form">
            <div className="hazard-stripes h-1.5 w-full" />
            <div className="p-7 sm:p-9">
              <h2 className="font-display text-2xl font-extrabold uppercase text-bright">Send a Message</h2>
              <div className="mt-8 grid gap-5 sm:grid-cols-2">
                <TextField label="Name" name="name" value={form.name} onChange={onChange} required testId="contact-name-input" />
                <TextField label="Email" name="email" type="email" value={form.email} onChange={onChange} required testId="contact-email-input" />
              </div>
              <div className="mt-5">
                <TextField label="Subject" name="subject" value={form.subject} onChange={onChange} testId="contact-subject-input" />
              </div>
              <div className="mt-5">
                <TextAreaField label="Message" name="message" value={form.message} onChange={onChange} rows={6} required placeholder="Tell us about your freight or your fleet…" testId="contact-message-textarea" />
              </div>
              <button
                type="submit"
                data-testid="contact-submit-btn"
                disabled={sending}
                className="mt-8 w-full bg-hazard px-6 py-4 font-mono text-sm font-bold uppercase tracking-[0.15em] text-asphalt transition-colors duration-300 hover:bg-hazard-dark disabled:opacity-50"
              >
                {sending ? "Sending…" : "Send Message"}
              </button>
            </div>
          </form>
        </Reveal>
      </section>
    </main>
  );
}
